// React 
import { useEffect, useCallback, useState } from "react";
import { useMediaQuery } from '@mui/material';

// Store 
import useProfileStore from "../../../store/profileStore.js";
import useSiteStore from "../../../store/siteStore.js";
import useAthleteStore from "../../../store/athleteStore.js";

// Page
import AthleteHome from "./athleteHome.js";
import AthleteHomeMobile from "./athleteHomeMobile.js";

// Other 
import HttpService from "../../../service/httpService/index.js";
import { addWeeklySchedule } from '../../../service/httpService/weeklySchedule/index.js';
import { formatCalendarWeekString, formatDate, getISOWeekStart, getWeek } from "../../../utils/index.js";

const DashboardAthlete = () => {
    const { userEmail } = useProfileStore();
    const { allOpen, mobile, selectedCalenderWeek, selectedTab, setAllOpen, setCalendarWeekString, setFailMessage, setFeedbacks, setLoading, setSelectedCalenderWeek, 
        setSelectedTab, setSnackbarOpenFail, setTableRowsOpen, setWeeklySchedule, setWeeklyScheduleLoading, tableRowsOpen, weeklySchedule, year } = useSiteStore();
    const { setTrainingSessions, trainingSessions } = useAthleteStore();
    const isTablet = useMediaQuery('(min-width:601px) and (max-width:1000px)');

    // Use mobile-like layout for both mobile and tablet
    const compactLayout = mobile || isTablet;

    const [athlete, setAthlete] = useState();
    const [weeklyScheduleRevisionCounter, setWeeklyScheduleRevisionCounter] = useState(0);
    const [athleteAge, setAthleteAge] = useState(null)
    const [coaches, setCoaches] = useState();
    const [parents, setParents] = useState();

    useEffect(() => {
        const savedSessions = localStorage.getItem('trainingSessions_athlete');
        if (savedSessions) {
            try {
                const parsed = JSON.parse(savedSessions);
                setTrainingSessions(parsed);
            } catch (e) {
                console.error('Failed to parse saved training sessions', e);
            }
        }
    }, [setTrainingSessions]);

    useEffect(() => {
        if (trainingSessions.length > 0) {
            localStorage.setItem('trainingSessions_athlete', JSON.stringify(trainingSessions));
        }
    }, [trainingSessions]);

    useEffect(() => {
        const savedSessions = localStorage.getItem('trainingSessions_athlete');
        if (savedSessions && selectedTab === 0) {
            try {
                const parsed = JSON.parse(savedSessions);
                if (parsed.length > 0) {
                    setSelectedTab(3);
                }
            } catch (e) {

            }
        }
    }, [selectedTab, setSelectedTab]);

    useEffect(() => {
        localStorage.setItem('selectedCalenderWeek', JSON.stringify(selectedCalenderWeek));
    }, [selectedCalenderWeek]);

    useEffect(() => {
        localStorage.setItem('selectedCalenderWeek', JSON.stringify(selectedCalenderWeek));
    }, [selectedCalenderWeek]);

    useEffect(() => {
        localStorage.setItem('selectedTab', JSON.stringify(selectedTab));
    }, [selectedTab]);

    /**
     * Holen uns den passenden Athleten mit seinen Daten und setzt diese
     */
    useEffect(() => {
        HttpService.athlete.getAthlete(userEmail)
            .then(response => {
                setAthlete(response.data);
                HttpService.coach.getCoachesOfAthlete(userEmail).then(response => {
                    setCoaches(response.data);
                    HttpService.parent.getParentsOfAthlete(userEmail).then(response => {
                        setParents(response.data);
                        setLoading(false);
                    }).catch(() => {
                    });
                }).catch(() => {
                });
            })
            .catch(() => {
            });
    }, [userEmail, setLoading]);

    /**
     * erstellt so viele Zeilen wie es Trainingseinheiten gibt
     */
    useEffect(() => {
        let values = [];
        if (weeklySchedule !== undefined && weeklySchedule.trainingSessions !== undefined) {
            for (let i = 0; i < weeklySchedule.trainingSessions.length; i++) {
                values.push(allOpen);  
            }
            setTableRowsOpen(values);
        }
    }, [weeklySchedule, allOpen, setTableRowsOpen]);

    const getWeeklySchedule = useCallback((email) => {
        setWeeklySchedule(undefined);
        setFeedbacks([]);
        setWeeklyScheduleLoading(true);
        HttpService.weeklySchedule.getWeeklyScheduleOfAthlete(email, year, selectedCalenderWeek)
            .then(response => {
                let weeklyScheduleResponse = response.data;
                setWeeklySchedule(weeklyScheduleResponse);
                setWeeklyScheduleLoading(false);
                HttpService.feedback.getAllFeedbacksOfWeekOfAthlete(email, year, selectedCalenderWeek)
                    .then(response => {
                        let feedbacksResponse = response.data;
                        setFeedbacks(feedbacksResponse);
                    })
                    .catch(() => {
                        setWeeklyScheduleLoading(false);
                    });
            })
            .catch(() => {
                setWeeklyScheduleLoading(false);
            });
    }, [selectedCalenderWeek, setFeedbacks, setWeeklySchedule, setWeeklyScheduleLoading, year]);

    const addTrainingSession = (initDate, dow) => {
        setTrainingSessions([...trainingSessions, {
            dayOfWeek: dow,
            trainingFocus: '',
            duration: 0,
            description: '',
            intensity: 0,
            date: initDate
        }]);
    }

    const handleChangeTrainingSession = (index, value, property) => {
        let values = [...trainingSessions];
        if (property === 'duration') {
            if (value < 0 || value.length > 3) {
                return;
            }
        }
        if (property === 'trainingFocus') {
            if (value === 8 || value === 7 || value === 9) {
                values[index]['duration'] = 0;
                values[index]['intensity'] = 0;
            }
        }
        values[index][property] = value;
        if (property === 'dayOfWeek') {
            values[index].date = getISOWeekStart(selectedCalenderWeek, year, value - 1);
        }
        setTrainingSessions(values);
    }

    const initTrainingSession = (initDate, dow) => {
        setTrainingSessions([{
            dayOfWeek: dow,
            trainingFocus: '',
            duration: 0,
            description: '',
            intensity: 0,
            date: initDate
        }]);
    }

    const prepCreateWeeklySchedule = (e) => {
        const savedSessions = localStorage.getItem('trainingSessions_athlete');
        if (savedSessions) {
            setSelectedTab(3);
            return;
        }

        setSelectedTab(3);

        // Only treat it as a calendar event if it has a date property
        if (e && e.date) {
            let date = new Date(e.date);
            const weekFromDate = getWeek(date);
            setSelectedCalenderWeek(weekFromDate);
            let weekDay = date.getDay();
            initTrainingSession(date, weekDay === 0 ? 7 : weekDay);
        } 
        else {
            initTrainingSession(getISOWeekStart(selectedCalenderWeek, year, 0), 1);
        }
    };
    const goToWochenplan = ()=>{
        setSelectedTab(0);
        localStorage.removeItem('trainingSessions_athlete');
        setTrainingSessions([]);
    }

    const handleSend = async () => {
        if (selectedCalenderWeek < getWeek(new Date()) || selectedCalenderWeek > 52) {
            setSnackbarOpenFail(true);
            setFailMessage("Fehlerhafte Kalenderwoche!");
            return;
        }

        for (let t of trainingSessions) {
            if (t.dayOfWeek <= 0) {
                setSnackbarOpenFail(true);
                setFailMessage(`Fehlerhafte Trainingseinheit (${formatDate(t.date)}): Wochentag!`);
                return;
            }

            if(t.trainingFocus <= 0 && t.description.length === 0){
                setSnackbarOpenFail(true);
                setFailMessage(`Fehlerhafte Trainingseinheit:Bitte alle Felder ausfüllen`);
                return;
            }

            if (t.trainingFocus <= 0) {
                setSnackbarOpenFail(true);
                setFailMessage(`Fehlerhafte Trainingseinheit (${formatDate(t.date)}): Fokus!`);
                return;
            }

            if (t.description.length === 0) {
                setSnackbarOpenFail(true);
                setFailMessage(`Fehlerhafte Trainingseinheit (${formatDate(t.date)}): Beschreibung!`);
                return;
            }
        }

        let calendarWeek = parseInt(selectedCalenderWeek, 10);
        let emails = [localStorage.getItem('email')];

        for (let session of trainingSessions) {
            session.duration = parseInt(session.duration, 10);
            session.date = getISOWeekStart(calendarWeek, year, session.dayOfWeek);
        }

        let weeklySchedule = {
            emails: emails,
            calendarWeek: calendarWeek,
            year: year,
            trainingSessions: trainingSessions,
        };

        try {
            await addWeeklySchedule(weeklySchedule);
        } catch (err) {
            setFailMessage(`Wochenplan KW ${calendarWeek} konnte nicht hinzugefügt werden!`);
            setSnackbarOpenFail(true);
            return;
        }

        setSelectedTab(0);
        setWeeklyScheduleRevisionCounter((prev) => prev + 1);
        localStorage.removeItem('trainingSessions_athlete');
    };

    useEffect(() => {
        if(athlete?.profile?.dateOfBirth) {
            const age = Math.floor((new Date() - new Date(athlete.profile.dateOfBirth)) / (1000 * 60 * 60 * 24 * 365.25));
            setAthleteAge(age);
        }
    }, [athlete])

    const openAndCloseAll = () => {
        let values = [...tableRowsOpen];
        for (let i = 0; i < values.length; i++) {
            values[i] = !allOpen;
        }
        setTableRowsOpen(values);
        setAllOpen(!allOpen);
    }

    const openAndCloseTableRow = (index) => {
        let values = [...tableRowsOpen];
        values[index] = !values[index];
        setTableRowsOpen(values);
    }

    /**
     * holen uns die ausgewählte Woche und formatieren diesen als String um
     */
    useEffect(() => {
        getWeeklySchedule(userEmail);
        setCalendarWeekString(formatCalendarWeekString(selectedCalenderWeek, year));
    }, [weeklyScheduleRevisionCounter, selectedCalenderWeek, setCalendarWeekString, userEmail, year, getWeeklySchedule]);

    if (compactLayout) {
        return (<AthleteHomeMobile 
            athlete={ athlete }  
            coaches={ coaches }  
            parents={ parents }
            startWeek={ selectedCalenderWeek }
            goToWochenplan = {goToWochenplan}
            openAndCloseAll={ openAndCloseAll }
            openAndCloseTableRow={ openAndCloseTableRow }
            prepCreateWeeklySchedule={ prepCreateWeeklySchedule }
            handleChangeTrainingSession={ handleChangeTrainingSession }
            addTrainingSession={ addTrainingSession }
            handleSend={ handleSend }
            isAbove18={ athleteAge >= 18 }
            />);
    }

    return (
        <AthleteHome
            athlete={ athlete }
            startWeek={ selectedCalenderWeek }
            openAndCloseAll={ openAndCloseAll }
            openAndCloseTableRow={ openAndCloseTableRow }
            coaches={ coaches }
            parents={ parents }
            prepCreateWeeklySchedule={ prepCreateWeeklySchedule }
            handleChangeTrainingSession={ handleChangeTrainingSession }
            addTrainingSession={ addTrainingSession }
            handleSend={ handleSend }
            goToWochenplan = {goToWochenplan}
            isAbove18={ athleteAge >= 18 }
        />
    )
}

export default DashboardAthlete;